import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { buildTariffExample, tariffExampleLines } from '../components/tariff/tariffExample';

/**
 * Tariff sheets as PDF.
 *
 * One page per package, so a customer with several packages gets a multi-page
 * document rather than tables running into each other. Layout and branding
 * follow pdfService (the invoice generator) so the two read as one system.
 */
class TariffPdfService {
    constructor() {
        this.logoBase64 = null;
    }

    async loadLogo() {
        if (this.logoBase64) return this.logoBase64;

        try {
            const response = await fetch('/admin/assets/images/logo.png');
            const blob = await response.blob();

            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = () => {
                    this.logoBase64 = reader.result;
                    resolve(this.logoBase64);
                };
                reader.onerror = () => resolve(null);
                reader.readAsDataURL(blob);
            });
        } catch (error) {
            return null;
        }
    }

    /**
     * @param {Array} sheets   one entry per package (the API's tariff payload)
     * @param {Object} context { title, subtitle, filename }
     */
    async download(sheets, context = {}) {
        await this.loadLogo();

        const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
        pdf.setProperties({
            title: context.title || 'SunEx Tariff',
            subject: 'Courier tariff',
            author: 'SUN-EX COURIER, CARGO & LOGISTICS PVT. LTD.',
            creator: 'SunEx Admin',
        });

        sheets.forEach((sheet, index) => {
            if (index > 0) pdf.addPage();
            this.renderSheet(pdf, sheet, context, index + 1, sheets.length);
        });

        pdf.save(context.filename || 'sunex-tariff.pdf');
    }

    renderSheet(pdf, sheet, context, pageNo, pageCount) {
        const pageWidth = pdf.internal.pageSize.getWidth();
        const margin = 14;
        let y = 14;

        if (this.logoBase64) {
            // Keep the logo's 8:5 aspect ratio; stretching it looks cheap on a
            // document that goes to customers.
            pdf.addImage(this.logoBase64, 'PNG', margin, y, 32, 20);
        }

        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(14);
        pdf.text(context.title || 'Tariff', pageWidth - margin, y + 7, { align: 'right' });

        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.setTextColor(90);
        if (context.subtitle) {
            pdf.text(context.subtitle, pageWidth - margin, y + 13, { align: 'right' });
        }
        pdf.text(
            `Generated ${new Date().toLocaleDateString('en-IN')}`,
            pageWidth - margin,
            y + 18,
            { align: 'right' }
        );
        pdf.setTextColor(0);

        y += 26;
        pdf.setDrawColor(220);
        pdf.line(margin, y, pageWidth - margin, y);
        y += 8;

        // Package heading
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(12);
        pdf.text(`${sheet.product.name} (${sheet.product.code})`, margin, y);
        y += 5;

        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8.5);
        pdf.setTextColor(90);
        const discountPct = Number(sheet.discount?.package_disc || 0);
        const meta = [
            sheet.product.type,
            sheet.product.tax_calculation_type === 'inclusive' ? 'Tax inclusive' : 'Tax exclusive',
            `Excess charged ${sheet.product.excess_unit}`,
            discountPct > 0 ? `${discountPct}% customer discount applied` : null,
        ].filter(Boolean);
        pdf.text(meta.join('  •  '), margin, y);
        y += 5;

        // The product description is deliberately omitted: it is free text that
        // usually restates the weight bands, which the table below lists exactly.
        pdf.setTextColor(0);
        y += 2;

        const hasDiscount = discountPct > 0;
        const money = (v) => (v === null || v === undefined ? '-' : `Rs. ${Number(v).toFixed(2)}`);

        const body = sheet.zones.map((z) => [
            // Name only: the code is an internal key, not customer-facing.
            z.zone,
            z.default_band,
            z.base_rate === null
                ? 'Not priced'
                : hasDiscount
                    ? `${money(z.net_base_rate)}\n(list ${money(z.base_rate)})`
                    : money(z.base_rate),
            z.bands?.length
                ? z.bands
                    .map((b) => `${b.range}: ${money(hasDiscount ? b.net_rate : b.rate)}`)
                    .join('\n')
                : '-',
        ]);

        autoTable(pdf, {
            startY: y,
            // Documents are charged per 0.5 kg, so the unit comes from the
            // product rather than being hardcoded as "per kg".
            head: [['Zone', 'Default Band', 'Base Rate',
                `Weight Bands with Additional Rate ${sheet.product.type === 'Document' ? 'per 0.5 kg' : 'per kg'}`]],
            body,
            theme: 'grid',
            styles: { fontSize: 8.5, cellPadding: 2.5, valign: 'middle', overflow: 'linebreak' },
            headStyles: { fillColor: [21, 44, 91], textColor: 255, fontStyle: 'bold' },
            columnStyles: {
                // Zone is a single short word now that the code is gone, so the
                // reclaimed width goes to the weight bands column.
                0: { cellWidth: 22 },
                1: { cellWidth: 32 },
                2: { cellWidth: 34, halign: 'right' },
                3: { cellWidth: 'auto' },
            },
            // Grey out zones with no price so the gaps are obvious at a glance.
            didParseCell: (data) => {
                if (data.section === 'body' && sheet.zones[data.row.index]?.not_priced) {
                    data.cell.styles.textColor = [150, 150, 150];
                }
            },
            margin: { left: margin, right: margin },
        });

        let footY = pdf.lastAutoTable.finalY + 6;
        pdf.setFontSize(8);
        pdf.setTextColor(90);
        const note = `The base rate covers any shipment up to the top of the default band. Beyond it, the weight-band rate is charged ${sheet.product.excess_unit} of excess weight.`;
        const noteLines = pdf.splitTextToSize(note, pageWidth - margin * 2);
        pdf.text(noteLines, margin, footY);
        footY += noteLines.length * 4 + 4;

        // A worked sum using this sheet's own rates, boxed so it reads as an
        // aside rather than more small print.
        const exampleLines = tariffExampleLines(buildTariffExample(sheet), 'Rs.');
        if (exampleLines.length > 0) {
            const boxHeight = exampleLines.length * 4.5 + 5;
            pdf.setFillColor(245, 247, 250);
            pdf.setDrawColor(21, 44, 91);
            pdf.setLineWidth(0.8);
            pdf.rect(margin, footY, pageWidth - margin * 2, boxHeight, 'F');
            pdf.line(margin, footY, margin, footY + boxHeight);

            let lineY = footY + 5;
            exampleLines.forEach((line, i) => {
                const isEmphasis = i === 0 || i === exampleLines.length - 1;
                pdf.setFont('helvetica', isEmphasis ? 'bold' : 'normal');
                pdf.setTextColor(isEmphasis ? 30 : 90);
                pdf.text(line, margin + 3, lineY);
                lineY += 4.5;
            });

            pdf.setFont('helvetica', 'normal');
            pdf.setLineWidth(0.2);
        }

        // Page footer
        const pageHeight = pdf.internal.pageSize.getHeight();
        pdf.setFontSize(7.5);
        pdf.setTextColor(130);
        pdf.text('SUN-EX COURIER, CARGO & LOGISTICS PVT. LTD.', margin, pageHeight - 10);
        pdf.text(`Page ${pageNo} of ${pageCount}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
        pdf.setTextColor(0);
    }
}

const tariffPdfService = new TariffPdfService();

export default tariffPdfService;
